import { Field, FieldProps } from 'formik';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { debounce } from '@mui/material/utils';
import Typography from '@mui/material/Typography';

import { MultiSelect } from 'common/components/FormField';
import { ITrainingSortBy, InputOptionTrainingsDocument } from 'graphql/types';
import { TInputOption } from 'common/types';

interface IProps {
    isFilter?: boolean;
    label?: string;
    ids?: string[];
    noTrainingsOption?: boolean;
    onChange?: (name: string, value: TInputOption[]) => void;
}

const BASE_QUERY_VARS = {
    first: 50,
    sortBy: { field: ITrainingSortBy.Title },
};

export const TrainingSelectField = ({
    isFilter,
    label,
    ids = [],
    noTrainingsOption,
    onChange,
    ...other
}: IProps & FieldProps) => {
    const [inputValue, setInputValue] = useState('');
    const [translate] = useTranslation();

    const [fetchTrainings, { data, loading, called }] = useLazyQuery(
        InputOptionTrainingsDocument
    );

    const { paginatedTrainings } = data || {};

    const trainings =
        paginatedTrainings?.edges.map((edge) => edge?.node).filter(Boolean) ||
        [];
    const trainingsCount = paginatedTrainings?.count || 0;

    const inputOptions: TInputOption[] = useMemo(() => {
        const options: TInputOption[] = [];

        if (noTrainingsOption) {
            options.push({
                id: 'noTrainings',
                name: translate('noTrainingsOption'),
                extraCategoryLabels: [],
                isInfo: false,
                divider: true,
            });
        }

        options.push(
            ...trainings.map(({ id, title, extraTrainingFieldsLabels }) => ({
                id,
                name: title,
                extraCategoryLabels: extraTrainingFieldsLabels || [],
                isInfo: false,
            }))
        );

        return options;
    }, [trainings, noTrainingsOption, translate]);

    const value: TInputOption[] = other.field.value;

    // Add value options to input options if they are not already in the input options
    value.forEach((valueOption) => {
        if (
            !inputOptions.some(
                (option: TInputOption) => option.id === valueOption.id
            )
        ) {
            const { id, name, extraCategoryLabels } = valueOption;

            inputOptions.push({
                id,
                name,
                extraCategoryLabels,
                isInfo: false,
            });
        }
    });

    if (trainingsCount > BASE_QUERY_VARS.first) {
        inputOptions.push({
            id: '',
            name: translate('moreResultsSelectSearch'),
            extraCategoryLabels: [],
            isInfo: true,
        });
    }

    const {
        form: { setFieldValue },
    } = other;

    const handleChange = (name: string, value: TInputOption[]) => {
        let newValue = [...value];

        // "No trainings" is mutually exclusive with specific trainings.
        // If it was already selected and a training is added, remove it.
        // If it is newly selected, remove all other selections.
        if (noTrainingsOption) {
            const noTrainingsIndex = newValue.findIndex(
                (option) => option.id === 'noTrainings'
            );

            if (noTrainingsIndex !== -1) {
                if (noTrainingsIndex === 0 && newValue.length > 1) {
                    newValue.splice(noTrainingsIndex, 1);
                } else {
                    newValue = newValue.filter(
                        (option) => option.id === 'noTrainings'
                    );
                }
            }
        }

        onChange?.(name, newValue);
        setFieldValue(name, newValue);
    };

    const fetch = useMemo(
        () =>
            debounce((inputValue: string) => {
                fetchTrainings({
                    variables: {
                        ...BASE_QUERY_VARS,
                        ids: !!inputValue ? undefined : ids,
                        q: inputValue || undefined,
                    },
                });
            }, 400),
        []
    );

    // Searching for results
    useEffect(() => {
        // The first query is done by opening, so we want to avoid calling it twice
        if (!called) return;

        fetch(inputValue);
    }, [inputValue, fetch]);

    // Fetch trainings when ids change
    useEffect(() => {
        if (!ids.length || !called) return;

        fetchTrainings({
            variables: {
                ...BASE_QUERY_VARS,
                ids: ids,
            },
        });
    }, [ids]);

    const handleOpen = () => {
        if (!!trainings.length) return;

        fetchTrainings({
            variables: {
                ...BASE_QUERY_VARS,
                ids: ids.length ? ids : undefined,
                q: inputValue || undefined,
            },
        });
    };

    return (
        <Field
            component={MultiSelect}
            filterOptions={(option: TInputOption) => option}
            getOptionDisplay={(option: TInputOption) => {
                if (option.id === 'noTrainings') {
                    return <>{translate('noTrainingsOption')}</>;
                }

                const extraCategoryString = option.extraCategoryLabels
                    .map((extraCategoryLabel) => extraCategoryLabel)
                    .join(', ');

                return (
                    <>
                        {option.name}

                        {!!extraCategoryString && (
                            <Typography
                                noWrap
                                color="grey.500"
                                component="div"
                                sx={{
                                    mt: 0.5,
                                    minWidth: '0',
                                    width: '100%',
                                }}
                                variant="caption"
                            >
                                {extraCategoryString}
                            </Typography>
                        )}
                    </>
                );
            }}
            getOptionDivider={(option: TInputOption) => option.divider}
            getOptionIsInfo={(option: TInputOption) => option.isInfo}
            getOptionKey={(option: TInputOption) => option.id}
            getOptionLabel={(option: TInputOption) => option.name}
            InputProps={{ label }}
            isOptionEqualToValue={(option: TInputOption, value: TInputOption) =>
                option.id === value.id
            }
            limitTags={5}
            loading={loading}
            loadingText={translate('loadingDots')}
            noOptionsText={translate(
                `noOptionsText.${isFilter ? 'filterTrainings' : 'trainings'}`
            )}
            options={inputOptions}
            onChange={handleChange}
            onInputChange={(
                _event: React.ChangeEvent<HTMLInputElement>,
                newInputValue: string
            ) => {
                setInputValue(newInputValue);
            }}
            onOpen={handleOpen}
            {...other}
        />
    );
};
